/**
 * Module dependencies.
 */

import React, { useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { ArrowLeftRight, CreditCard, FileText, Folder, LayoutPanelTop, PieChart, Settings, Truck } from 'lucide-react';

import { Outlet } from 'react-router-dom';
import { Dashboard } from './dashboard';
import { appRoutes } from 'app/routes';
import { useProjects } from 'app/hooks/requests/projects/use-projects';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { compileMenu } from 'app/core/utils/menu';
import { Project } from 'app/types/project';

/**
 * Create project menu.
 */

function createProjectMenu(project: Project, translate: TFunction) {
  return [
    {
      key: `${project.id}_overview`,
      pathname: resolveAppPath(appRoutes.dashboard.projects.project.overview, { projectId: project.id }),
      icon: <PieChart className={'h-4 w-4'} />,
      label: translate('projects.menu.overview')
    },
    {
      key: `${project.id}_budgets`,
      pathname: resolveAppPath(appRoutes.dashboard.projects.project.budgets, { projectId: project.id }),
      icon: <LayoutPanelTop className={'h-4 w-4'} />,
      label: translate('projects.menu.budgets')
    },
    {
      key: `${project.id}_suppliers`,
      pathname: resolveAppPath(appRoutes.dashboard.projects.project.suppliers, { projectId: project.id }),
      icon: <Truck className={'h-4 w-4'} />,
      label: translate('projects.menu.suppliers')
    },
    {
      key: `${project.id}_proposals`,
      pathname: resolveAppPath(appRoutes.dashboard.projects.project.proposals, { projectId: project.id }),
      icon: <FileText className={'h-4 w-4'} />,
      label: translate('projects.menu.proposals')
    },
    {
      key: `${project.id}_movements`,
      pathname: resolveAppPath(appRoutes.dashboard.projects.project.movements, { projectId: project.id }),
      icon: <ArrowLeftRight className={'h-4 w-4'} />,
      label: translate('projects.menu.movements')
    },
    {
      key: `${project.id}_paymentTypes`,
      pathname: resolveAppPath(appRoutes.dashboard.projects.project.paymentTypes, { projectId: project.id }),
      icon: <CreditCard />,
      label: translate('projects.menu.paymentTypes')
    },
    {
      key: `${project.id}_settings`,
      pathname: resolveAppPath(appRoutes.dashboard.projects.project.settings, { projectId: project.id }),
      icon: <Settings className={'h-4 w-4'} />,
      label: translate('projects.menu.settings')
    }
  ];
}

/**
 * Export `SkinProjects` component.
 */

export function SkinProjects(): JSX.Element {
  const [translate] = useTranslation();
  const { data } = useProjects();
  const items = useMemo(
    () =>
      compileMenu([
        {
          key: 'overview',
          pathname: appRoutes.dashboard.projects.base,
          icon: <PieChart className={'h-4 w-4'} />,
          label: translate('projects.menu.overview')
        },
        ...(data?.map(item => ({
          key: `project_${item.id}`,
          pathname: undefined,
          icon: <Folder className={'h-4 w-4'} />,
          label: item.name,
          children: createProjectMenu(item, translate)
        })) ?? [])
      ]),
    [data, translate]
  );

  return (
    <Dashboard menu={items}>
      <Outlet />
    </Dashboard>
  );
}
